<script lang="ts">
  import { Router, Route } from 'svelte-routing';
  import { onMount } from 'svelte';
  import { auth } from './lib/stores/auth';
  import { api } from './lib/api/client';
  import { websocket } from './lib/stores/websocket';
  import Nav from './lib/components/Nav.svelte';
  import ConfirmDialog from './lib/components/ConfirmDialog.svelte';
  import Login from './routes/Login.svelte';
  import Map from './routes/Map.svelte';
  import Groups from './routes/Groups.svelte';
  import Person from './routes/Person.svelte';
  import Places from './routes/Places.svelte';
  import Crash from './routes/Crash.svelte';
  import CheckIn from './routes/CheckIn.svelte';
  import CheckInAlert from './routes/CheckInAlert.svelte';
  import Sos from './routes/Sos.svelte';
  import Timeline from './routes/timeline/Timeline.svelte';
  import Activities from './routes/activities/Activities.svelte';
  import ActivityDetail from './routes/activities/ActivityDetail.svelte';
  import Progress from './routes/activities/Progress.svelte';
  import Health from './routes/activities/Health.svelte';
  import Heatmap from './routes/activities/Heatmap.svelte';
  import GoalPage from './routes/activities/Goal.svelte';
  import Recap from './routes/activities/Recap.svelte';
  import Invite from './routes/Invite.svelte';
  import Forgot from './routes/Forgot.svelte';
  import Reset from './routes/Reset.svelte';
  import SettingsLayout from './routes/settings/Layout.svelte';
  import Share from './routes/Share.svelte';
  import Devices from './routes/Devices.svelte';
  import FirstGroup from './lib/components/FirstGroup.svelte';
  import { t } from './lib/i18n/index.ts';

  // A share link's page is for someone without an account, so it shows no navigation.
  const shared = window.location.pathname.startsWith('/s/');

  let initialized = $state(false);

  onMount(async () => {
    await auth.init();

    // Signing in or out reopens the socket, since the token rides the URL. A
    // tab managing a child has none: its session reaches settings alone.
    if (!api.acting) {
      websocket.connect();
      auth.subscribe(() => websocket.reconnect());
    }

    initialized = true;
  });
</script>

{#if !initialized}
  <div class="flex items-center justify-center min-h-screen">
    <div class="animate-pulse text-text-muted text-lg">{t('appStarting')}</div>
  </div>
{:else}
  <ConfirmDialog />
  <Router>
    {#if !shared}<Nav />{/if}
    <main class="flex-1 flex flex-col min-h-0 overflow-y-auto">
      {#if api.acting}
        <!-- A parent managing a child sees the child's settings and nothing else (spec 001 FR-100). -->
        <Route path="/settings/*"><SettingsLayout /></Route>
        <Route path="/groups"><Groups /></Route>
        <Route path="/devices"><Devices /></Route>
        <Route path="/people/:userId">
          {#snippet children({ params }: { params: Record<string, string> })}
            <Person userId={params.userId} />
          {/snippet}
        </Route>
        <Route path="/*"><SettingsLayout /></Route>
      {:else}
      <Route path="/login"><Login /></Route>
      <Route path="/forgot"><Forgot /></Route>
      <Route path="/reset/:token">
        {#snippet children({ params }: { params: Record<string, string> })}
          <Reset token={params.token} />
        {/snippet}
      </Route>
      <Route path="/groups"><FirstGroup><Groups /></FirstGroup></Route>
      <Route path="/people/:userId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><Person userId={params.userId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/places"><FirstGroup><Places /></FirstGroup></Route>
      <Route path="/devices"><FirstGroup><Devices /></FirstGroup></Route>
      <Route path="/timeline/me"><FirstGroup><Timeline /></FirstGroup></Route>
      <Route path="/timeline/:groupId/:userId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><Timeline groupId={params.groupId} userId={params.userId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/activities"><FirstGroup><Activities /></FirstGroup></Route>
      <Route path="/activities/progress"><FirstGroup><Progress /></FirstGroup></Route>
      <Route path="/activities/health"><FirstGroup><Health /></FirstGroup></Route>
      <Route path="/activities/heatmap"><FirstGroup><Heatmap /></FirstGroup></Route>
      <Route path="/activities/goals/:goalId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><GoalPage goalId={params.goalId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/activities/recap/:month">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><Recap month={params.month} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/activities/:groupId/:userId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><Activities groupId={params.groupId} userId={params.userId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/activity/:activityId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><ActivityDetail activityId={params.activityId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/activity/:groupId/:activityId">
        {#snippet children({ params }: { params: Record<string, string> })}
          <FirstGroup><ActivityDetail groupId={params.groupId} activityId={params.activityId} /></FirstGroup>
        {/snippet}
      </Route>
      <Route path="/check-in"><FirstGroup><CheckIn /></FirstGroup></Route>
      <Route path="/check-in/:id">
        {#snippet children({ params }: { params: Record<string, string> })}
          <CheckInAlert id={params.id} />
        {/snippet}
      </Route>
      <Route path="/sos/:id">
        {#snippet children({ params }: { params: Record<string, string> })}
          <Sos id={params.id} />
        {/snippet}
      </Route>
      <Route path="/crash/:id">
        {#snippet children({ params }: { params: Record<string, string> })}
          <Crash id={params.id} />
        {/snippet}
      </Route>
      <Route path="/settings/*"><SettingsLayout /></Route>
      <Route path="/s/:token">
        {#snippet children({ params }: { params: Record<string, string> })}
          <Share token={params.token} />
        {/snippet}
      </Route>
      <Route path="/invite/:token">
        {#snippet children({ params }: { params: Record<string, string> })}
          <Invite token={params.token} />
        {/snippet}
      </Route>
      <Route path="/"><FirstGroup><Map /></FirstGroup></Route>
      {/if}
    </main>
  </Router>
{/if}
